import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Signup from './Signup';
import { FaPlayCircle, FaHeart, FaLock, FaStar, FaGem, FaBell, FaComment, FaUsers, FaFilter, } from 'react-icons/fa';
import { BiSearch } from 'react-icons/bi';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaThumbsUp, FaStarHalfAlt, FaQuoteLeft } from 'react-icons/fa';
import './SplashScreen.css';
import { GiCrystalBall, GiSpy, GiHeartBeats, GiPalm, GiMoonOrbit, GiCardJoker } from "react-icons/gi";
import { Link } from 'react-router-dom';
import LoginPopup from "./LoginPopup";
import { Helmet } from "react-helmet";

const specialties = [
  { name: "Vedic Astrology", icon: <GiCrystalBall /> },
  { name: "Marriage Compatibility", icon: <GiHeartBeats /> },
  { name: "Palm Reading", icon: <GiPalm /> },
  { name: "Tarot Reading", icon: <GiCardJoker /> },
  { name: "Horoscope Analysis", icon: <GiMoonOrbit /> },
  { name: "Face Reading", icon: <GiSpy /> } // New specialty added
];
const faqData = [
  {
    question: "How does Varankal Matrimony ensure privacy?",
    answer:
      "We prioritize your privacy by using encrypted data storage and giving users control over their personal information visibility."
  },
  {
    question: "Is registration on Varankal Matrimony free?",
    answer:
      "Yes, basic registration is free. Premium features such as direct messaging and profile boosting are available at affordable rates."
  },
  {
    question: "Can I filter matches based on community and profession?",
    answer:
      "Absolutely. Our platform provides advanced filters to help you find matches based on community, profession, age, and more."
  },
  {
    question: "Is there any customer support for profile issues?",
    answer:
      "Yes, our support team is available 24/7 to assist with any queries related to profiles, payments, or privacy settings."
  },
  {
    question: "Does Varankal Matrimony offer astrology support and bookings?",
    answer:
      "Yes, we provide access to verified astrologers for Kundli matching and personalized consultations. You can also schedule astrology sessions directly from your dashboard."
  },
  {
    question: "What is mentorship support and match score?",
    answer:
      "Our platform offers mentorship guidance for marriage readiness and compatibility. We also provide a profile match score based on interests, values, and family preferences."
  },
  {
    question: "Can I view who visited my profile?",
    answer:
      "Yes, with a premium subscription you can view who visited your profile and when they visited."
  },
  {
    question: "How do I report or block a user?",
    answer:
      "You can report or block a user directly from their profile page. Our moderation team will review reports promptly to ensure safety."
  }
];

const features = [
  {
    icon: <FaLock />,
    title: "Privacy First",
    desc: "Your data is fully secure with advanced privacy protection."
  },
  {
    icon: <FaStar />,
    title: "High Success Rate",
    desc: "Thousands have successfully found their perfect match here."
  },
  {
    icon: <FaFilter />,
    title: "Advanced Filters",
    desc: "Search easily by community, profession, and other traits."
  },
  {
    icon: <FaHeart />,
    title: "Mentorship",
    desc: "Expert guidance to help you build lasting relationships."
  }
];


const steps = [
  {
    number: 1,
    title: 'Register for Mentor Support',
    description: 'Sign up to get access to experienced mentors who will guide you through the matchmaking journey.',
    color: '#1B1A34',
  },
  {
    number: 2,
    title: 'Get Personalized Guidance',
    description: 'Mentors help you create an impressive biodata and suggest filters based on your preferences.',
    color: '#1B1A34',
  },
  {
    number: 3,
    title: 'Mentor-Assisted Match Search',
    description: 'With mentor support, explore matches and get advice on selecting suitable profiles.',
    color: '#1B1A34',
  },
  {
    number: 4,
    title: 'Support till Marriage',
    description: 'Mentors assist you with the final steps including contacting families and proceeding forconfidence.',
    color: '#1B1A34',
  },
];

const featuresData = [
  {
    id: "perfect-matches",
    icon: <FaHeart className="text-white text-5xl" />,
    title: "Perfect Matches",
    description: "Send connection requests to profiles that perfectly align with your preferences.",
    bgColor: "bg-gradient-to-br from-pink-400 to-pink-500",
  },
  {
    icon: <BiSearch className="text-white text-5xl" />,
    title: "Match Score",
    description: "Get updated match scores daily as new members join the platform.",
  },
  {
    icon: <FaGem className="text-white text-5xl" />,
    title: "Recommended Profiles",
    description: "Follow members to receive updates and view their recommended profiles.",
  },
  {
    id: "chat",
    icon: <FaComment className="text-white text-5xl" />,
    title: "Instant Chat",
    description: "Start real-time conversations to get to know your matches better.",
    bgColor: "bg-gradient-to-br from-purple-400 to-purple-500",
  },
  {
    id: "notification",
    icon: <FaBell className="text-white text-5xl" />,
    title: "Notifications",
    description: "Stay informed with alerts for new matches, messages, and special updates.",
    bgColor: "bg-gradient-to-br from-yellow-400 to-yellow-500",
  },
  {
    icon: <FaUsers className="text-white text-5xl" />,
    title: "Restriction Settings",
    description: "Manage privacy settings such as photo protection and anti-spam options.",
  }
];

const SplashScreen = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSignup, setShowSignup] = useState(false); // Toggle between login and signup
  const [showHelp, setShowHelp] = useState(false); // Show Help Modal
  const [stories, setStories] = useState([]);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(true); // To show loading state for success stories
  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();
  const scrollRef = useRef();
  const [activeIndex, setActiveIndex] = useState(null);
  const [topReviews, setTopReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [featuredMembers, setFeaturedMembers] = useState([]);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [hideNav, setHideNav] = useState(false);
  let lastScrollY = window.scrollY;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > lastScrollY) {
        // scrolling down
        setHideNav(true);
      } else {
        // scrolling up
        setHideNav(false);
      }
      lastScrollY = window.scrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  window.addEventListener("scroll", function () {
    const navbar = document.querySelector(".navbar-varankal");

    if (!navbar) return; // ✅ PREVENT CRASH

    if (window.scrollY > 50) {
      navbar.style.background = "rgba(255, 255, 255, 0.98)";
      navbar.style.boxShadow = "0 2px 12px rgba(0, 0, 0, 0.15)";
    } else {
      navbar.style.background = "rgba(255, 255, 255, 0.92)";
      navbar.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.08)";
    }
  });


  const toggle = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const images = [
    "/Images/h2.jpg",
    "/Images/h2.jpg",
    "/Images/h3.jpg",
    "/Images/h4.jpg",
  ];
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft -= 320; // adjust scroll distance
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += 320;
    }
  };
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? featuredMembers.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === featuredMembers.length - 1 ? 0 : prev + 1));
  };
  const matrimonyProcess = [
    "Signup",
    "Login",
    "Registration",
    "Create Profile",
    "Partner Preferences",
  ];
  const stepDetails = [
    "Create your account by providing basic details.",
    "Login with your credentials to proceed.",
    "Fill in your basic details to complete the registration process.",
    "Build your profile with personal information.",
    "Set your preferences to find the perfect match.",
  ];

  const toggleHelp = () => {
    setShowHelp(!showHelp);
  };

  const closeModalOnClickOutside = (e) => {
    if (e.target.classList.contains("help-popup-overlay")) {
      setShowHelp(false);
    }
  };
  useEffect(() => {
    if (showHelp) {
      document.addEventListener("click", closeModalOnClickOutside);
    }
    return () => {
      document.removeEventListener("click", closeModalOnClickOutside);
    };
  }, [showHelp]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleReviewButton = () => {
    navigate('/review');
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, email, message } = formData;
    if (!name || !email || !message) {
      alert("Please fill in all fields.");
      return;
    }
    alert("Thanks for reaching out! We'll get back to you soon.");
    setFormData({ name: "", email: "", message: "" });
    setShowHelp(false);
  };
  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await axios.get("https://back.varankal.com/api/success-stories");
        setStories(response.data); // Assuming the backend sends an array of stories
        setLoading(false);
      } catch (error) {
        console.error("Error fetching stories:", error);
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch("https://back.varankal.com/api/reviews");
      if (!response.ok) throw new Error("Failed to fetch reviews");
      const data = await response.json();
      setReviews(data);                     // keep the full list if needed
      const firstFive = data.slice(0, 5);
      setTopReviews(firstFive);
      const avg = firstFive.reduce((sum, r) => sum + r.rating, 0) / firstFive.length;
      const avgOutOf10 = (avg * 2).toFixed(1);     // convert 5‑star to 10‑point
      setAverageRating(avgOutOf10);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleProfileClick = async (userId) => {
    console.log("User ID:", userId); // Add this line to check if userId is being passed correctly
    if (!userId) {
      alert("User ID is not available.");
      return;
    } try {
      const response = await axios.get(`https://back.varankal.com/user/view-profile/${userId}`, { withCredentials: true });

      // ✅ Proceed if access is granted
      if (response.data.profile) {
        navigate(`/userpage/${userId}`);
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        alert(error.response.data.error || "Upgrade your plan to view more profiles.");
      } else {
        alert("An error occurred while accessing the profile.");
      }
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000);

    return () => clearInterval(interval); // Cleanup
  }, []);

  useEffect(() => {
    const fetchFeaturedProfiles = async () => {
      try {
        const res = await axios.get('https://back.varankal.com/auth/featured-profiles');
        if (res.data?.success && res.data?.data) {
          setFeaturedMembers(res.data.data);
        } else {
          console.error('No featured data found.');
        }
      } catch (err) {
        console.error('Error fetching featured profiles:', err);
      }
    };

    fetchFeaturedProfiles();
  }, []);

  useEffect(() => {
    const profileCreated = localStorage.getItem('profileCreated');
    if (profileCreated === 'false') {
      setShowProfileModal(true);
    }
  }, []);

  return (
    <div className="splash-screen11">
      <Helmet>
        <title>Varankal.com | Tamil Matrimony Website | Find Tamil Bride & Groom Online</title>
        <meta
          name="description"
          content="Varankal.com is Tamil Nadu’s trusted matrimony website helping you find verified Tamil brides and grooms online. Join for free and begin your perfect love story today."
        />
        <meta
          name="keywords"
          content="Tamil Matrimony, Tamil Marriage Website, Tamil Bride and Groom, Matrimony in Tamil Nadu, Tamil Matrimony for NRIs, Online Matrimonial Services, Tamil Marriage Portal, Varankal Matrimony"
        />
      </Helmet>

      <div className={`navbar-varankal ${hideNav ? "navbar-hide" : ""}`}>
        <div className="navbar-left-varankal">
          <img
            src="/Images/icon tm.png"
            alt="Varankal Logo"
            className="logol-varankal"
          />
        </div>

        <div className="navbar-right-varankal">
          <button
            className="navbtn-varankal text-btnv"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              navigate("/aboutus");
            }}
          >
            About Us
          </button>

          <button
            className="navbtn-varankal text-btnv"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              navigate("/contactus");
            }}
          >
            Help
          </button>

          <button
            className="navbtn-varankal login-btn"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              setShowLogin(true);
            }}
          >
            Login
          </button>
        </div>

        {showLogin && <LoginPopup onClose={() => setShowLogin(false)} />}
      </div>
      <section className="main-sectionv">
        <div className="content-boxv">
          <div className="left-contentv">
            <motion.h1 initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="main-headingv"
            >
              Find Your Perfect Tamil Life Partner on Varankal
            </motion.h1>

            <p className="sub-headingv">
              Tamil Nadu’s Trusted Matrimony Platform            </p>

            <button
              className="find-partner-btnv"
              onClick={() => setShowSignup(true)}
            >
              Find Partner
            </button>

            {showSignup && <Signup setShowSignup={setShowSignup} />}
          </div>
        </div>

        <div className="bottom-sectionv">
          <div className="combined-sectionv">
            <p className="matchingv"># Matching Profiles: 12,450+</p>
            <div className="rating-sectionv">
              <div className="avatarsv">
                {topReviews.map((review, i) => (
                  <img
                    key={i}
                    src={review.image}
                    alt={review.user}
                    onError={(e) => (e.target.src = "https://via.placeholder.com/80")}
                  />
                ))}
              </div>
              <div className="starsv">
                {Array.from({ length: 5 }, (_, i) => (
                  <span key={i} className="starv">
                    {i < Math.floor(averageRating / 2)
                      ? "★"
                      : i < averageRating / 2
                        ? "★"
                        : "☆"}
                  </span>
                ))}
                <span className="scorev">{averageRating} / 10</span>
              </div>
            </div>
            <p className="successv">❤️ Success Stories: 3,200+</p>
          </div>
        </div>
      </section>

      <section className="featured-sectionK22">
        <h2 class="featured-titlek22">Featured Profiles</h2>
        <p class="featured-descriptionk22">
          Welcome to Varankal.com, the most trusted and culturally rooted Tamil Matrimony Website designed to connect hearts across Tamil Nadu and the world.
        </p>
        <div className="slider-wrapperK22">
          <div
            className="slider-trackK22"
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
            }}
          >
            {featuredMembers.map((member, index) => {
              const {
                name = 'Unknown',
                age = 'N/A',
                gender = 'Not specified',
                occupation = 'Profession not provided',
                location = 'Location not available',
                profilePic,
              } = member.profileDetails || {};

              const userId = member.userId;

              const isBase64 = profilePic && !profilePic.startsWith('http');
              const profileImageSrc = profilePic
                ? isBase64
                  ? `data:image/jpeg;base64,${profilePic}`
                  : profilePic
                : 'https://via.placeholder.com/100';

              return (
                <div className="slideK22" key={index}>
                  <div className="profile-cardK22">
                    <div className="profile-image-containerK22">
                      <img
                        src={profileImageSrc}
                        alt={`${name}'s profile`}
                        className="profile-imageK22"
                      />
                      <div className="profile-detailsK22">
                        <h3>{name}, {age}</h3>
                        <p>{occupation}</p>
                        <p>{location}</p>
                      </div>
                    </div>
                    <button
                      className="view-profile-btnK22"
                      onClick={() => handleProfileClick(userId)}
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="manual-buttonsK22">
            <button onClick={handlePrev} className="manual-btnK22">⟵ Prev</button>
            <button onClick={handleNext} className="manual-btnK22">Next ⟶</button>
          </div>
        </div>
      </section>

      <div className="feature-section1123">
        <div className="feature-header1123">
          <p className="feature-subtitle1123">FEATURES TO</p>
          <h2 className="feature-title1123">Choose Varankal Matrimony</h2>
        </div>
        <div className="feature-grid1123">
          {featuresData.map((feature, index) => (
            <div className="feature-card1123" key={index}>
              <div className="feature-icon1123">{feature.icon}</div>
              <div>
                <h3 className="feature-name1123">{feature.title}</h3>
                <p className="feature-desc1123">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <section className="success-stories-section">
        <h2 className="success-main-title">Success Stories at Varankal Matrimony</h2>

        <div className="success-stories-wrapper">
          <div className="success-text-section">
            <blockquote className="story-quote">
              "Thanks to Varankal Matrimony, I met my soulmate. The platform’s personalized approach truly makes a difference."
              <footer>— Sita & Ram</footer>
            </blockquote>

            <p className="story-description">
              Join thousands who have found love, trust, and lifelong happiness through Varankal Matrimony.
              Your story could be the next one we celebrate!
            </p>

            <button
              className="cta-btn"
              onClick={() => {
                window.scrollTo(0, 0);
                window.location.href = "/successstory";
              }}
            >
              Read More Stories
            </button>

          </div>

          <div className="success-carousel-section">
            <FaChevronLeft className="arrow-left" onClick={scrollLeft} />
            <FaChevronRight className="arrow-right" onClick={scrollRight} />

            <div className="success-grid" ref={scrollRef}>
              {stories.map((story, index) => (
                <div key={index} className="success-card">
                  <div className="media-wrapper">
                    {story.fileType === "image" ? (
                      <img
                        src={story.fileUrl}
                        alt={story.name}
                        className="success-image"
                      />
                    ) : story.fileType === "video" ? (
                      <div className="video-container">
                        <FaPlayCircle className="play-icon" />
                        <video src={story.fileUrl} controls className="success-video" />
                      </div>
                    ) : null}

                    <div className="overlay-content">
                      <p className="success-description-card">{story.description}</p>
                      <FaHeart className="heart-icon" />
                    </div>
                  </div>

                  <h4 className="success-name">{story.name}</h4>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      <div className="varankal-wrapper">
        <div className="varankal-left">
          <h2 className="varankal-title7">
            Why Thousands Trust Varankal Matrimony
          </h2>
          <p className="varankal-desc">
            Verified profiles ensure trust and security. Our platform connects you with culturally compatible matches that align with your values.
            Get personalized astrology guidance to find a harmonious bond, and access expert mentorship to support your journey toward a successful marriage.
            <br />
            Varankal Matrimony is more than matchmaking — it's a complete support system for meaningful relationships.
          </p>
          <button
            className="varankal-btn"
            onClick={() => {
              window.scrollTo(0, 0);
              window.location.href = "/aboutus";
            }}
          >
            See Our Process
          </button>
        </div>
        <div className="varankal-right-features">
          {features.map((item, index) => (
            <div className="varankal-feature-box" key={index}>
              <div className="icon-boxr">{item.icon}</div>
              <div>
                <h3 className="feature-title">{item.title}</h3>
                <p className="feature-desc">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="knowus-container123">
        <div className="knowus-text123">
          <h2>Who We Are</h2>
          <p className="knowus-quote123">
            At <strong>Varankal</strong>, we’re more than just a matchmaking platform — we’re your partner in finding meaningful relationships.
            Our mission is to connect hearts through shared values, culture, and compatibility. With a trusted process and personalized support,
            we’re here to help you take the next step in your journey toward love and lifelong companionship.
          </p>

          <div className="rating-section">
            <div className="avatars">
              {topReviews.map((review, i) => (
                <img
                  key={i}
                  src={review.image} // Use directly
                  alt={review.user}
                  onError={(e) => (e.target.src = "https://via.placeholder.com/80")}
                />
              ))}
            </div>

            <div className="stars">
              {Array.from({ length: 5 }, (_, i) => (
                <span key={i} className="star">
                  {i < Math.floor(averageRating / 2)
                    ? "★"
                    : i < averageRating / 2
                      ? "★"
                      : "☆"}
                </span>
              ))}
              <span className="score">{averageRating} / 10</span>
            </div>
          </div>
        </div>
        <div className="knowus-review123">
          <div className="image-wrapper123">
            <img src="/Images/istockphoto-1175680546-612x612.jpg" alt="Happy Users" className="side-image123" />
            <div className="image-label123">Trusted by Thousands</div>
          </div>

          <motion.div
            className="swipeable-button123"
            whileTap={{ scale: 0.95 }}
            onClick={handleReviewButton}
          >
            <button
              className="swipe-button123"
              onClick={() => {
                window.scrollTo(0, 0);
                window.location.href = "/review";
              }}
            >
              Read More Stories
            </button>
          </motion.div>
        </div>
      </div>

      <div className="review-section">
        <h2 className="cta-user-feedback-title">
          What Our Uers Says  💕
        </h2>
        <div className="review-container">

          {loading && <p style={{ textAlign: "center" }}>Loading reviews...</p>}
          {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
          {reviews.map((review, i) => (
            <motion.div
              key={i}
              className={`review-card ${i === index ? "active" : "inactive"}`}
              whileHover={{ scale: 1.05 }}
            >
              <img
                src={review.image}
                alt={review.user}
                className="user-image"
                onError={(e) => (e.target.src = "https://via.placeholder.com/100")}
              />

              <FaQuoteLeft className="quote-icon" />
              <p className="review-text">"{review.text}"</p>
              <div className="stars">
                {Array.from({ length: Math.floor(review.rating) }, (_, i) => (
                  <FaStar key={i} className="star" />
                ))}
                {review.rating % 1 !== 0 && <FaStarHalfAlt className="star" />}
              </div>
              <h4 className="user-name">- {review.user}</h4>
              <p className="user-meta"><FaThumbsUp /> {review.impressiveType}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="download-container">
        <p className="download-text">Varankal Matrimony App – Connect On-The-Go with Verified Matches</p>
        <div className="store-buttons">
          <a href="#" target="_blank" rel="noopener noreferrer">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
              className="store-badge"
            />
          </a>
          <a href="#" target="_blank" rel="noopener noreferrer">
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              className="store-badge"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;